import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  Drawer,
  Typography,
  IconButton,
  Chip,
  Box,
  Badge,
  Fade,
  Grow,
  CircularProgress,
  TextField,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import {
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Wifi as WifiIcon,
} from "@material-ui/icons";
import NotificationsOffIcon from "@material-ui/icons/NotificationsOff";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import { Tooltip } from "@material-ui/core";
import { FixedSizeList } from "react-window";

import { StatsAction } from "../../actions/StatsAction";
import { VehicleAction } from "../../actions/vehicle";
import { decryptAesObj } from "../CommonComponents/AESencryptdecrypt";
import "../UserManagement/SubUserManagement/subUser.css";
import notificationBus from "../../utils/notificationBus";

import {
  NOTIFICATION_TYPES,
  STATUS_FILTERS,
  normalizeNotification,
  todayStr,
  toISOStart,
  toISOEnd,
  nowTime,
  WS_TYPE_TO_API_KEY,
} from "./notificationConstants";
import { useNotificationStyles, useTooltipStyles } from "./notificationStyles";
import MapModal from "./MapModal";
import ManagedAlertCard from "./ManagedAlertCard";
import { useInfiniteScroll } from "./useInfiniteScroll";
import muteState from "./muteState";
import { LocksAction } from "../../actions/lock";
import { ALERT_PAIRS, OffsetPopper } from "../TripConfiguration/util";
import { C } from "../RouteConfiguration/util";
import { getAccessType, getAvailableModes } from "../../utils/accessType"; // ← adjust to wherever you put this fil

let _tankerCache = null;

const LISTBOX_PADDING = 8; // px

const ListboxComponent = React.forwardRef(
  function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const itemData = React.Children.toArray(children);
    const itemCount = itemData.length;
    const itemSize = 44;

    const getChildSize = () => {
      return itemCount * itemSize;
    };

    const getHeight = () => {
      if (itemCount > 8) {
        return 8 * itemSize;
      }
      return getChildSize() + 2 * LISTBOX_PADDING;
    };

    return (
      <div ref={ref} {...other}>
        <FixedSizeList
          data={itemData}
          height={getHeight()}
          width="100%"
          itemSize={itemSize}
          outerElementType={React.forwardRef((props, ref) => (
            <div ref={ref} {...props} />
          ))}
          itemCount={itemCount}
          overscanCount={5}
        >
          {({ index, style }) => {
            return React.cloneElement(itemData[index], {
              style: {
                ...style,
                top: style.top + LISTBOX_PADDING,
              },
            });
          }}
        </FixedSizeList>
      </div>
    );
  },
);

const NOTIFICATION_MODES = {
  GPS: "gps",
  LOCK: "lock",
};

// Helper to format lock alert types to readable messages
const formatAlertMessage = (alertType) => {
  const alertMessages = {
    TEMPERING_ALERT: "🚨 Tampering Detected",
    WIRE_CUT: "✂️ Wire Cut",
    UNAUTHORIZED_STOPS: "⛔ Unauthorized Stop",
    OVER_SPEED: "⚡ Over Speed Alert",
    ROUTE_DEVIATION_ALERT: "🚗 Route Deviation",
    LOCK_OPENED: "🔓 Lock Opened",
    TRIP_END: "✅ Trip End",
    LOW_BATTERY: "🔋 Low Battery",
    LOCK_OFFLINE: "📡 Offline",
    SOURCE_EXIT: "🚪 Source Exit",
    DESTINATION_ENTRY: "🏁 Destination Entry",
    MIDPOINT_ENTRY: "📍 Midpoint Entry",
    MIDPOINT_EXIT: "📍 Midpoint Exit",

  };
  return alertMessages[alertType] || alertType || "Lock Alert";
};

const NotificationDrawer = React.memo(
  ({ events = [], setEvents = () => { } }) => {
    const classes = useNotificationStyles();
    const tooltipClasses = useTooltipStyles();
    /* ── Drawer open/tab state ── */
    const [open, setOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("live"); // "live" | "search"
    const [autoFetch, setAutoFetch] = useState(false);

    /* ── Search filter state ── */
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [selectedType, setSelectedType] = useState(null);
    const [fromDate, setFromDate] = useState(todayStr);
    const [toDate, setToDate] = useState(todayStr);
    const [tankerList, setTankerList] = useState(_tankerCache || []);

    const [muted, setMuted] = useState(muteState.get());

    /* ── API / fetch state ── */
    const [apiRaw, setApiRaw] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetched, setFetched] = useState(false);

    /* ── Map modal state ── */
    const [mapModal, setMapModal] = useState({
      open: false,
      lat: null,
      lng: null,
      label: null,
      alertTime: null,
    });

    /* ── Managed-alert state (search tab) ── */
    const [managedAlerts, setManagedAlerts] = useState([]);
    const [expandedId, setExpandedId] = useState(null);
    const [statusFilter, setStatusFilter] = useState("all");
    const [searchQ, setSearchQ] = useState("");
    const [locks, setLocks] = useState([]);
    const [locksLoading, setLocksLoading] = useState(false);
    const [selectedLock, setSelectedLock] = useState(null);
    const [selectedLockAlert, setSelectedLockAlert] = useState(null);

    /* ── Access control ── */
    const accessType = useMemo(() => getAccessType(), []);
    const availableModes = useMemo(() => {
      const modes = getAvailableModes(accessType).map((m) =>
        m === "digitalLock" ? "lock" : m,
      );
      return modes.length > 0 ? modes : ["gps"]; // safety fallback
    }, [accessType]);
    const showModeToggle = accessType === "BOTH";

    const [notificationMode, setNotificationMode] = useState(availableModes[0]);

    /* ── Derived lists ── */
    const liveNotifications = useMemo(
      () => (Array.isArray(events) ? events.map(normalizeNotification) : []),
      [events],
    );

    // const apiNotifications = useMemo(
    //   () => (Array.isArray(apiRaw) ? apiRaw.map(normalizeNotification) : []),
    //   [apiRaw],
    // );

    const apiNotifications = useMemo(() => {
      if (!Array.isArray(apiRaw)) return [];
      return apiRaw
        .map(normalizeNotification)
        .sort((a, b) => new Date(b.eventTime) - new Date(a.eventTime)); // 🔥 latest first
    }, [apiRaw]);

    useEffect(() => {
      const unsub = muteState.subscribe((val) => setMuted(val));
      return unsub;
    }, []);

    /* ── Sync apiNotifications → managedAlerts (preserve action state) ── */
    useEffect(() => {
      setManagedAlerts((prev) => {
        const prevMap = Object.fromEntries(prev.map((a) => [a._id, a]));
        return apiNotifications.map((n) => ({
          ...n,
          status: prevMap[n._id]?.status ?? "open",
          trail: prevMap[n._id]?.trail ?? [],
        }));
      });
    }, [apiNotifications]);

    const fetchLocks = async () => {
      try {
        setLocksLoading(true);
        const res = await LocksAction.getLockList({ pageNo: 0, pageSize: 0 });
        if (res.data && Array.isArray(res.data) && res.data.length > 0) {
          console.log("sdfsdjflskd", res.data);
          const sortOrder = { IDLE: 0, ENGAGED: 1 };
          const sorted = [...res.data].sort((a, b) => {
            const aOrder = sortOrder[a.status] ?? 2;
            const bOrder = sortOrder[b.status] ?? 2;
            return aOrder - bOrder;
          });
          setLocks(sorted);
        } else {
          setLocks([]);
        }
      } catch (error) {
        setLocks([]);
      } finally {
        setLocksLoading(false);
      }
    };

    const LOCK_ALERT_OPTIONS = ALERT_PAIRS.flat()
      .filter(Boolean)
      .map((item) => ({
        value: item.key,
        label: item.label,
      }))
      .concat([
        { value: "WIRE_CUT", label: "Wire Cut" },
        { value: "LOW_BATTERY", label: "Low Battery" },
      ]);

    useEffect(() => {
      if (notificationMode === NOTIFICATION_MODES.LOCK && locks.length === 0) {
        fetchLocks();
      }
    }, [notificationMode]);

    const handleModeChange = useCallback(
      (mode) => {
        if (mode === notificationMode) return;
        setNotificationMode(mode);
        setFetched(false);
        setApiRaw([]);
        setExpandedId(null);
        setSelectedVehicle(null);
        setSelectedType(null);
        setSelectedLock(null);
        setSelectedLockAlert(null);
      },
      [notificationMode],
    );

    /* ── Sync liveNotifications → managedAlerts for Live tab (preserve action state) ── */
    // const liveManagedAlerts = useMemo(() => {
    //   return liveNotifications.map((n) => ({
    //     ...n,
    //     status: "open",
    //     trail: [],
    //   }));
    // }, [liveNotifications]);

    const LOCK_ALERT_TYPES = new Set([
      "TAMPERING_ALERT",
      "WIRE_CUT",
      "UNAUTHORIZED_STOPS",
      "UNAUTHORIZED_STOP_ALERT",
      "OVER_SPEED",
      "ROUTE_DEVIATION_ALERT",
      "LOCK_OPENED",
      "LOCK_CLOSED",
      "TRIP_END",
      "TRIP_START",
      "LOW_BATTERY",
      "LOCK_OFFLINE",
      "LOCK_DISCONNECTED",
      "LOCK_TAMPERED",
      "SOURCE_EXIT",
      "DESTINATION_ENTRY",
      "MIDPOINT_ENTRY",
      "MIDPOINT_EXIT",
    ]);

    const liveManagedAlerts = useMemo(() => {
      const isLockType = (type) => LOCK_ALERT_TYPES.has(type);

      return liveNotifications
        .filter((n) => {
          if (notificationMode === "gps") return !isLockType(n.notificationType);
          if (notificationMode === "lock") return isLockType(n.notificationType);
          return true;
        })
        .map((n) => ({ ...n, status: "open", trail: [] }));
    }, [liveNotifications, notificationMode]);

    console.log("liveManagedAlerts", liveManagedAlerts)

    /* ── KPI counts (search tab only) ── */
    const kpiCounts = useMemo(
      () => ({
        openTotal: managedAlerts.filter((a) => a.status === "open").length,
      }),
      [managedAlerts],
    );

    /* ── Filtered + searched list (search tab) ── */
    const visibleManaged = useMemo(() => {
      const q = searchQ.toLowerCase();
      return managedAlerts.filter((a) => {
        if (statusFilter === "open" && a.status !== "open") return false;
        if (statusFilter === "closed" && a.status !== "closed") return false;
        if (statusFilter === "rejected" && a.status !== "rejected")
          return false;
        if (
          q &&
          !(a.deviceName || "").toLowerCase().includes(q) &&
          !(a.message || "").toLowerCase().includes(q)
        )
          return false;
        return true;
      });
    }, [managedAlerts, statusFilter, searchQ]);

    const {
      visibleItems: pagedManaged,
      hasMore: hasMoreManaged,
      sentinelRef: managedSentinel,
    } = useInfiniteScroll(visibleManaged, 20);

    // For live tab
    const {
      visibleItems: pagedLive,
      hasMore: hasMoreLive,
      sentinelRef: liveSentinel,
    } = useInfiniteScroll(liveManagedAlerts, 20);

    /* ── Action handler (resolve / reject / immob) ── */
    const handleAction = useCallback((id, act, remark) => {
      setManagedAlerts((prev) =>
        prev.map((a) => {
          if (a._id !== id) return a;
          const updated = {
            ...a,
            trail: [
              ...a.trail,
              { by: "Admin", role: "Admin", act, remark, ts: nowTime() },
            ],
          };
          if (act === "resolve") updated.status = "closed";
          if (act === "reject") updated.status = "rejected";
          return updated;
        }),
      );
      setExpandedId(null);
    }, []);

    /* ── Card expand/collapse ── */
    const handleToggle = useCallback((id) => {
      setExpandedId((prev) => (prev === id ? null : id));
      setTimeout(
        () =>
          document
            .getElementById(`managed-card-${id}`)
            ?.scrollIntoView({ behavior: "smooth", block: "nearest" }),
        50,
      );
    }, []);

    /* ── Badge count ── */
    const badgeCount = liveNotifications.length;

    /* ── Auto-switch to Live tab on new WS event ── */
    const prevLiveCount = useRef(0);
    useEffect(() => {
      if (open && liveNotifications.length > prevLiveCount.current)
        setActiveTab("live");
      prevLiveCount.current = liveNotifications.length;
    }, [liveNotifications.length, open]);

    /* ── Open/close drawer ── */
    const toggleDrawer = useCallback(
      (val) => () => {
        setOpen(val);
        if (!val) setEvents([]);
      },
      [setEvents],
    );

    /* ── Reset search state when drawer closes ── */
    useEffect(() => {
      if (!open) {
        setFetched(false);
        setApiRaw([]);
        setExpandedId(null);
      }
    }, [open]);

    /* ── Fetch vehicle list ── */
    const fetchTankerList = () => {
      if (!availableModes.includes("gps")) return;  
      if (_tankerCache) return;
      let siteIds = [];
      if (decryptAesObj(localStorage.getItem("data")).sites?.length > 0)
        decryptAesObj(localStorage.getItem("data")).sites.forEach((el) =>
          siteIds.push(el.id),
        );
      VehicleAction.fetchVehicleListV6({
        pageNo: 0,
        pageSize: 0,
        companyId: decryptAesObj(localStorage.getItem("data")).companyId,
        number: "",
        vehicleType: "",
        driver: "",
        site: { id: "" },
        vendorId: "",
        siteIds: [1070, 1071, 1072],
        userId: JSON.parse(localStorage.getItem("data")).id,
        onlyGpsVehicle: null,
        allVehicle: true,
        forDropDown: true,
      }).then((response) => {
        _tankerCache = response || []; // ← save to module-level cache
        setTankerList(_tankerCache);
      });
    };

    /* ── Fetch API notifications ── */
    const fetchNotifications = useCallback(async () => {
      setLoading(true);
      try {
        if (notificationMode === "gps") {
          // GPS MODE - existing logic
          const userId = JSON.parse(localStorage.getItem("data")).coreUserId;
          const basePayload = {
            userId,
            pageNo: 0,
            pageSize: 0,
            ...(fromDate && { startDate: toISOStart(fromDate) }),
            ...(toDate && { endDate: toISOEnd(toDate) }),
          };

          const payload = {
            ...basePayload,
            ...(selectedVehicle && { deviceId: selectedVehicle.gpsRefId }),
            ...(selectedType && { notificationType: selectedType.value }),
          };

          const res = await StatsAction.fetchAlertsData(payload);
          if (res?.responseCode === 200 && res?.data?.data) {
            setApiRaw(res.data.data);
          } else {
            setApiRaw([]);
          }
        } else {
          const payload = {
            alertTypeList: selectedLockAlert ? [selectedLockAlert.value] : [],
            lockId: selectedLock?.id || null,
            lockNumber: selectedLock?.lockNumber || null,
            fromDate: fromDate
              ? new Date(toISOStart(fromDate)).getTime()
              : null,
            toDate: Date.now(),
            tripId: null,
            pageNo: 0,
            pageSize: 100,
          };

          const res = await LocksAction.getLockAlerts(payload);

          if (res && res.data && Array.isArray(res.data)) {
            // Map lock alerts to notification format
            // console.log("APi Response alerts for locks", res.data)
            const mapped = res.data.filter(el => el.alertType).map((alert) => ({
              _id: `lock-${alert.id}`,
              deviceId: alert.lockId,
              deviceName: alert.lockNumber || "Unknown Lock",
              lockNumber: alert.lockNumber,
              notificationType:
                WS_TYPE_TO_API_KEY[alert?.alertType] ||
                alert.alertType ||
                "LOCK_ALERT", // ← translated
              message: formatAlertMessage(alert.alertType),
              eventTime: new Date(alert.eventTime).toISOString(),
              lat: alert.latitude || 0,
              lng: alert.longitude || 0,
              latitude: alert.latitude || 0,
              longitude: alert.longitude || 0,
              tripId: alert.tripId || null,
              status: "open",
              alertType: alert.alertType,
              sourceId: alert.id,
            }));
            setApiRaw(mapped);
          } else {
            setApiRaw([]);
          }
        }
      } catch (e) {
        console.error("NotificationDrawer fetchNotifications error:", e);
        setApiRaw([]);
      } finally {
        setLoading(false);
        setFetched(true);
      }
    }, [
      fromDate,
      toDate,
      selectedVehicle,
      selectedType,
      selectedLock,
      selectedLockAlert,
      notificationMode,
    ]);

    const handleBellClick = () => {
      fetchTankerList();
      setOpen(true);
    };

    /* ── notificationBus registration ── */
    useEffect(() => {
      notificationBus.register((notificationType, filters = {}) => {
        setOpen(true);
        setActiveTab("search");
        setAutoFetch(true);
        const today = new Date().toISOString().split("T")[0];
        setFromDate(filters.fromDate || today);
        setToDate(filters.toDate || today);
        setSelectedVehicle(null);
        setSelectedLock(null);

        if (filters.mode) {
          setNotificationMode(filters.mode);
        }

        if (notificationType) {
          const found = NOTIFICATION_TYPES.find(
            (t) => t.value === notificationType,
          );
          setSelectedType(found || null);

          const foundLockAlert = LOCK_ALERT_OPTIONS.find(
            (t) => t.value === notificationType,
          );
          setSelectedLockAlert(foundLockAlert || null);
        } else {
          setSelectedType(null);
          setSelectedLockAlert(null);
        }
      });
      return () => {
        notificationBus._handler = null;
      };
    }, []);

    /* ── Auto-fetch triggered by notificationBus ── */
    useEffect(() => {
      if (autoFetch && open && activeTab === "search") {
        fetchNotifications();
        setAutoFetch(false);
      }
    }, [autoFetch, open, activeTab]);

    /* ── On mount: load vehicle list ── */
    // useEffect(() => {
    //   if (availableModes.includes("gps")) fetchTankerList();
    // }, [availableModes]);

    /* ── Open map modal helper ── */
    const openMapModal = useCallback(
      (data) => setMapModal({ open: true, ...data }),
      [],
    );
    const closeMapModal = useCallback(
      () => setMapModal({ open: false, lat: null, lng: null, label: null }),
      [],
    );

    /* ── Shared empty state renderer ── */
    const EmptyState = ({ isLive }) => (
      <Fade in timeout={350}>
        <Box textAlign="center" p={4}>
          <NotificationsIcon style={{ fontSize: 42, color: "#c7d2fe" }} />
          <Typography
            variant="h6"
            style={{
              marginTop: 12,
              color: "#64748b",
              fontSize: "0.95rem",
              fontWeight: 600,
            }}
          >
            {isLive ? "No live notifications" : "No notifications found"}
          </Typography>
          <Typography
            variant="body2"
            style={{ color: "#a0aec0", marginTop: 4 }}
          >
            {isLive
              ? "Live alerts from WebSocket will appear here."
              : "Try adjusting the filters and searching again."}
          </Typography>
        </Box>
      </Fade>
    );

    return (
      <Box>
        {/* ── Bell button ── */}
        <Tooltip title="View Notifications" arrow>
          <Badge badgeContent={badgeCount} className={classes.badge} max={99}>
            <IconButton
              // onClick={toggleDrawer(true)}
              onClick={handleBellClick}
              className={classes.notificationButton}
              size="small"
            >
              <NotificationsIcon style={{ fontSize: 28, color: "#003a6b" }} />
            </IconButton>
          </Badge>
        </Tooltip>

        {/* ── Drawer ── */}
        <Drawer
          anchor="right"
          open={open}
          onClose={toggleDrawer(false)}
          classes={{ paper: classes.drawerPaper }}
          transitionDuration={300}
        >
          {/* ── Header ── */}
          <div className={classes.header}>
            <div className={classes.headerContent}>
              <Typography variant="h6" className={classes.title}>
                Notifications
              </Typography>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {/* <Chip
                  label={
                    activeTab === "live"
                      ? liveNotifications.length
                      : visibleManaged.length
                  }
                  style={{
                    background: "rgba(255,255,255,0.92)",
                    color: "#082D6B",
                    fontWeight: 700,
                    height: 25,
                    fontSize: "0.78rem",
                  }}
                /> */}
                <Tooltip
                  title={muted ? "Unmute live alerts" : "Mute live alerts"}
                  arrow
                >
                  <IconButton
                    size="small"
                    onClick={() => muteState.toggle()}
                    style={{
                      color: muted ? "rgba(255,255,255,0.5)" : "#fff",
                      padding: 4,
                    }}
                  >
                    {muted ? (
                      <NotificationsOffIcon style={{ fontSize: 18 }} />
                    ) : (
                      <NotificationsActiveIcon style={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </Tooltip>
                <IconButton
                  onClick={toggleDrawer(false)}
                  style={{ color: "#fff" }}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </div>
            </div>
          </div>
          {showModeToggle && (
            <Box
              display="flex"
              alignItems="center"
              bgcolor="#f1f5f9"
              borderRadius={12}
              p={0.5}
              mx={1.5}
              my={1}
            >
              <Box
                onClick={() => handleModeChange("gps")}
                flex={1}
                textAlign="center"
                py={1}
                sx={{
                  cursor: "pointer",
                  borderRadius: 10,
                  background:
                    notificationMode === "gps"
                      ? "linear-gradient(135deg, #082D6B, #0F4C9A)"
                      : "transparent",
                  color: notificationMode === "gps" ? "#fff" : "#475569",
                  fontWeight: 700,
                  fontSize: "14px",
                  transition: "all 0.25s ease",
                  boxShadow:
                    notificationMode === "gps"
                      ? "0 2px 8px rgba(8,45,107,0.25)"
                      : "none",
                }}
              >
                GPS Vehicles
              </Box>

              <Box
                onClick={() => handleModeChange("lock")}
                flex={1}
                textAlign="center"
                py={1}
                sx={{
                  cursor: "pointer",
                  borderRadius: 10,
                  background:
                    notificationMode === "lock"
                      ? "linear-gradient(135deg, #082D6B, #0F4C9A)"
                      : "transparent",
                  color: notificationMode === "lock" ? "#fff" : "#475569",
                  fontWeight: 700,
                  fontSize: "14px",
                  transition: "all 0.25s ease",
                  boxShadow:
                    notificationMode === "lock"
                      ? "0 2px 8px rgba(8,45,107,0.25)"
                      : "none",
                }}
              >
                Digital Locks
              </Box>
            </Box>
          )}

          {/* ── Tab bar ── */}
          <div className={classes.tabBar}>
            <button
              className={`${classes.tab} ${activeTab === "live" ? classes.tabActive : ""}`}
              onClick={() => setActiveTab("live")}
            >
              <WifiIcon style={{ fontSize: 14 }} />
              Live
              {badgeCount > 0 && (
                <span className={classes.liveDot}>{badgeCount}</span>
              )}
            </button>
            <button
              className={`${classes.tab} ${activeTab === "search" ? classes.tabActive : ""}`}
              onClick={() => setActiveTab("search")}
            >
              <SearchIcon style={{ fontSize: 14 }} />
              Search History
              {fetched && kpiCounts.openTotal > 0 && (
                <span
                  style={{
                    background: "#FFEBEE",
                    color: "#C62828",
                    border: "1px solid #FFCDD2",
                    borderRadius: 20,
                    padding: "1px 6px",
                    fontSize: "0.58rem",
                    fontWeight: 800,
                    marginLeft: 2,
                  }}
                >
                  {kpiCounts.openTotal}
                </span>
              )}
            </button>
          </div>

          {/* ══════════════════════════════════════
            LIVE TAB — same ManagedAlertCard design
        ══════════════════════════════════════ */}
          {activeTab === "live" && (
            <div className={classes.list}>
              {pagedLive.length > 0 ? (
                <>
                  {pagedLive.map((n, i) => (
                    <ManagedAlertCard
                      key={n._id || i}
                      n={n}
                      index={i}
                      isExpanded={expandedId === n._id}
                      onToggle={handleToggle}
                      onAction={handleAction}
                      onMapClick={openMapModal}
                      classes={classes}
                      type={notificationMode == "gps" ? "VEHICLE" : "LOCK"}
                    />
                  ))}
                  {hasMoreLive && (
                    <div
                      ref={liveSentinel}
                      style={{ padding: 16, textAlign: "center" }}
                    >
                      <CircularProgress
                        size={20}
                        style={{ color: "#082D6B" }}
                      />
                    </div>
                  )}
                </>
              ) : (
                <EmptyState isLive />
              )}
            </div>
          )}

          {/* ══════════════════════════════════════
            SEARCH HISTORY TAB
        ══════════════════════════════════════ */}
          {activeTab === "search" && (
            <>
              {/* Filter panel */}
              <div className={classes.filterPanel}>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <FilterListIcon style={{ fontSize: 14, color: "#082D6B" }} />
                  <span
                    style={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      color: "#082D6B",
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                    }}
                  >
                    Filters
                  </span>
                </div>

                <div className={classes.filterRow}>
                  <div className={classes.filterGroup}>
                    <span className={classes.filterLabel}>
                      {notificationMode === "gps" ? "Vehicle" : "Locks"}
                    </span>
                    <Autocomplete
                      options={
                        notificationMode === "gps"
                          ? tankerList
                          : locks.map((lock) => ({
                            lockNumber: lock.lockNumber,
                            serialNumber: lock.serialNumber,
                            gpsRefId: lock.id,
                            elockType: lock.elockType,
                            batteryStatus: lock.batteryStatus,
                            status: lock.status,
                          }))
                      }
                      className="sl-filter-autocomplete"
                      ListboxComponent={ListboxComponent}
                      getOptionLabel={(opt) =>
                        notificationMode === "gps"
                          ? opt.number || ""
                          : opt.serialNumber
                            ? `${opt.serialNumber} (${opt.lockNumber})`
                            : opt.lockNumber || ""
                      }
                      value={notificationMode === "gps" ? selectedVehicle : selectedLock}
                      getOptionSelected={(opt, val) =>
                        notificationMode === "gps"
                          ? opt.number === val.number
                          : opt.lockNumber === val.lockNumber
                      }
                      onChange={(_, val) =>
                        notificationMode === "gps"
                          ? setSelectedVehicle(val)
                          : setSelectedLock(val)
                      }
                      size="small"
                      PopperComponent={(popperProps) => (
                        <OffsetPopper
                          {...popperProps}
                          style={{
                            ...popperProps.style,
                            width: notificationMode === "lock" ? 375 : 280,
                          }}
                        />
                      )}
                      PaperComponent={({ children, ...paperProps }) => (
                        <div
                          {...paperProps}
                          style={{
                            background: "#fff",
                            borderRadius: 10,
                            boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                            border: "1px solid #e8eef8",
                            overflow: "hidden",
                            width: notificationMode === "lock" ? 375 : 280,
                          }}
                        >
                          {children}
                        </div>
                      )}
                      renderOption={(opt) => (
                        /* ...same renderOption as before... */
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "2px 0" }}>
                          <div style={{ width: 7, height: 7, borderRadius: "50%", background: C.primary, marginTop: 5, flexShrink: 0 }} />
                          <div>
                            {notificationMode === "gps" ? (
                              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                                {opt.number}
                              </div>
                            ) : (
                              <>
                                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: C.text }}>
                                  {opt.serialNumber}{" "}
                                  <span style={{ color: "#94a3b8", fontWeight: 500 }}>
                                    ({opt.lockNumber})
                                  </span>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      padding: "1px 7px",
                                      borderRadius: 10,
                                      fontSize: 9.5,
                                      fontWeight: 700,
                                      letterSpacing: "0.3px",
                                      background: "linear-gradient(135deg, #EDE4FF, #DDD6FE)",
                                      color: "#6D28D9",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {opt?.elockType?.lockType?.replace(/_/g, " ") || "Lock With Keypad"}
                                  </span>
                                </div>
                                {(opt.batteryStatus !== undefined && opt.batteryStatus !== null) && (
                                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                                    🔋 {opt.batteryStatus}%
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder={`Select ${notificationMode === "gps" ? "Vehicle" : "Lock"}`}
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            style: { fontSize: "0.8rem", borderRadius: 8, background: "#f8fafc" },
                          }}
                        />
                      )}
                    />
                  </div>
                  <div className={classes.filterGroup}>
                    <span className={classes.filterLabel}>Type</span>
                    <Autocomplete
                      options={
                        notificationMode === "gps"
                          ? NOTIFICATION_TYPES
                          : LOCK_ALERT_OPTIONS
                      }
                      getOptionLabel={(opt) => opt.label}
                      value={
                        notificationMode === "gps"
                          ? selectedType
                          : selectedLockAlert
                      }
                      className="sl-filter-autocomplete"
                      getOptionSelected={(opt, val) => opt.value === val.value}
                      onChange={(_, val) =>
                        notificationMode === "gps"
                          ? setSelectedType(val)
                          : setSelectedLockAlert(val)
                      }
                      size="small"
                      PopperComponent={OffsetPopper}
                      PaperComponent={({ children, ...paperProps }) => (
                        <div
                          {...paperProps}
                          style={{
                            background: "#fff",
                            borderRadius: 10,
                            boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                            border: "1px solid #e8eef8",
                            overflow: "hidden",
                          }}
                        >
                          {children}
                        </div>
                      )}
                      renderOption={(opt) => (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 8,
                            padding: "2px 0",
                          }}
                        >
                          <div
                            style={{
                              width: 7,
                              height: 7,
                              borderRadius: "50%",
                              background: C.primary,
                              marginTop: 5,
                              flexShrink: 0,
                            }}
                          />
                          <div>
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: C.text,
                              }}
                            >
                              {opt.label}
                            </div>
                          </div>
                        </div>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="All Types"
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            style: {
                              fontSize: "0.8rem",
                              borderRadius: 8,
                              background: "#f8fafc",
                            },
                          }}
                        />
                      )}
                    />
                  </div>
                </div>

                <div
                  className={classes.filterRow}
                  style={{ alignItems: "flex-end" }}
                >
                  <div className={classes.filterGroup}>
                    <span className={classes.filterLabel}>From Date</span>
                    <input
                      type="date"
                      value={fromDate}
                      // onChange={(e) => setFromDate(e.target.value)}
                      max={toDate}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFromDate(val);
                        if (val > toDate) setToDate(val);
                      }}
                      className="gf-date-input"
                    />
                  </div>
                  <div className={classes.filterGroup}>
                    <span className={classes.filterLabel}>To Date</span>
                    <input
                      type="date"
                      value={toDate}
                      // onChange={(e) => setToDate(e.target.value)}
                      min={fromDate}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val < fromDate) {
                          setToDate(fromDate);
                        } else {
                          setToDate(val);
                        }
                      }}
                      className="gf-date-input"
                    />
                  </div>
                </div>

                <button
                  onClick={fetchNotifications}
                  disabled={loading}
                  style={{
                    background: loading ? "#94a3b8" : "#082D6B",
                    color: "#fff",
                    border: "none",
                    borderRadius: 9,
                    padding: "0 16px",
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    cursor: loading ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    height: 37,
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                  }}
                >
                  {loading ? (
                    <CircularProgress size={12} style={{ color: "#fff" }} />
                  ) : (
                    <SearchIcon style={{ fontSize: 14 }} />
                  )}
                  Search
                </button>
              </div>

              {/* Status filter bar — only when results exist */}
              {/* {fetched && !loading && managedAlerts.length > 0 && (
              <div className={classes.statusBar}>
                {STATUS_FILTERS.map(sf => (
                  <button
                    key={sf.key}
                    className={`${classes.sFilterBtn} ${statusFilter === sf.key ? classes.sFilterBtnActive : ""}`}
                    onClick={() => { setStatusFilter(sf.key); setExpandedId(null); }}
                  >
                    {sf.label}{sf.key === "open" ? ` (${kpiCounts.openTotal})` : ""}
                  </button>
                ))}
                <input
                  className={classes.searchBox}
                  placeholder="🔍 Vehicle / Message…"
                  value={searchQ}
                  onChange={e => { setSearchQ(e.target.value); setExpandedId(null); }}
                />
              </div>
            )} */}

              {/* Scrollable results */}
              <div className={classes.list}>
                {loading && (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    pt={5}
                  >
                    <CircularProgress
                      size={32}
                      style={{ color: "#082D6B", marginBottom: 12 }}
                    />
                    <Typography variant="body2" style={{ color: "#94a3b8" }}>
                      Fetching notifications…
                    </Typography>
                  </Box>
                )}

                {!loading && !fetched && (
                  <Fade in timeout={350}>
                    <Box textAlign="center" p={4}>
                      <FilterListIcon
                        style={{ fontSize: 42, color: "#c7d2fe" }}
                      />
                      <Typography
                        variant="h6"
                        style={{
                          marginTop: 12,
                          color: "#64748b",
                          fontSize: "0.95rem",
                          fontWeight: 600,
                        }}
                      >
                        Set filters and search
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{ color: "#a0aec0", marginTop: 4 }}
                      >
                        Apply filters above to fetch historical notifications.
                      </Typography>
                    </Box>
                  </Fade>
                )}

                {!loading && fetched && visibleManaged.length > 0 && (
                  <>
                    {pagedManaged.map((n, i) => (
                      <ManagedAlertCard
                        key={n._id}
                        n={n}
                        index={i}
                        isExpanded={expandedId === n._id}
                        onToggle={handleToggle}
                        onAction={handleAction}
                        onMapClick={openMapModal}
                        classes={classes}
                        type={notificationMode == "gps" ? "VEHICLE" : "LOCK"}
                      />
                    ))}
                    {hasMoreManaged && (
                      <div
                        ref={managedSentinel}
                        style={{ padding: 16, textAlign: "center" }}
                      >
                        <CircularProgress
                          size={20}
                          style={{ color: "#082D6B" }}
                        />
                      </div>
                    )}
                  </>
                )}

                {!loading && fetched && visibleManaged.length === 0 && (
                  <EmptyState />
                )}
              </div>
            </>
          )}
        </Drawer>

        {/* ── Map modal (shared between tabs) ── */}
        <MapModal
          open={mapModal.open}
          onClose={closeMapModal}
          lat={mapModal.lat}
          lng={mapModal.lng}
          label={mapModal.label}
          notificationType={mapModal.notificationType}
          tooltipClasses={tooltipClasses}
          // alertTime={alertTime}
          alertTime={mapModal.alertTime}
        />
      </Box>
    );
  },
);

export default NotificationDrawer;
